import React, { useState, useRef, useEffect } from 'react'
import CustomIcons from './CustomIcons'
import StringSearchMatchDisplayer from './logic/StringSearchMatchDisplayer'
import theme from '../../styles/theme'
import {
  ComboBoxContainer,
  ComboBoxToggle,
  ComboBoxInput,
  ComboBoxDisplay,
  IconWrapper,
  ComboBoxMenu,
  ComboBoxMenuItem,
  ItemIcon,
} from './styles/StandardComboBox.styles'

// Interface for data items that can be searched and selected
export interface SearchableItem {
  id: string | number
  name: string
  icon?: string
  isCreateOption?: boolean
  disabled?: boolean
  tooltip?: string
}

// Props interface for the StandardComboBox component
interface StandardComboBoxProps {
  data: SearchableItem[]
  onChange: (selectedItems: SearchableItem[]) => void
  placeholder?: string
  maxSelected?: number
  alwaysShowList?: boolean
  valueList?: SearchableItem[]
  singleSelection?: boolean
  style?: React.CSSProperties
  maxHeight?: number
  allowCreate?: boolean
  onCreate?: (newItemName: string) => void
  disabled?: boolean
  readOnly?: boolean
  'data-testid'?: string
}

const StandardComboBox: React.FC<StandardComboBoxProps> = ({
  data,
  onChange,
  placeholder = 'Select...',
  maxSelected = 1,
  alwaysShowList = false,
  valueList = [],
  singleSelection = true,
  style,
  maxHeight,
  allowCreate = false,
  onCreate,
  disabled = false,
  readOnly = false,
  'data-testid': dataTestId,
}) => {
  const [searchText, setSearchText] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [filteredItems, setFilteredItems] = useState<SearchableItem[]>([])
  const [hasIcons, setHasIcons] = useState<boolean>(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Get the selected value (single selection)
  const selectedValue = valueList.length > 0 ? valueList[0] : null
  const isInteractive = !disabled && !readOnly

  useEffect(() => {
    if (data && data.length > 0) {
      setHasIcons(data.some((item) => item.icon))
    }
  }, [data])

  useEffect(() => {
    if (!isInteractive && isOpen) {
      setIsOpen(false)
      setSearchText('')
    }
  }, [isInteractive, isOpen])

  useEffect(() => {
    if (!isOpen) {
      setSearchText('')
      return
    }

    if (!searchText && alwaysShowList) {
      setFilteredItems(data)
    } else if (!searchText) {
      setFilteredItems(data)
      return
    }

    const lowercasedSearchText = searchText.toLowerCase()
    const filtered = data.filter((item) =>
      item.name.toLowerCase().includes(lowercasedSearchText),
    )

    if (allowCreate && searchText && filtered.length === 0) {
      const exactMatchExists = data.some(
        (item) => item.name.toLowerCase() === lowercasedSearchText,
      )
      if (!exactMatchExists) {
        const createItem: SearchableItem = {
          id: `__create__${searchText}`,
          name: `Create "${searchText}"`,
          isCreateOption: true,
        }
        setFilteredItems([createItem])
        return
      }
    }

    setFilteredItems(filtered)
  }, [searchText, data, alwaysShowList, allowCreate, isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggleClick = () => {
    if (!isInteractive) return

    setIsOpen(!isOpen)
    if (!isOpen) {
      // Focus input when opening
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isInteractive) return
    setSearchText(e.target.value)
    if (!isOpen) {
      setIsOpen(true)
    }
  }

  const handleSelectItem = (item: SearchableItem) => {
    if (!isInteractive) return
    // Don't allow selection of disabled items
    if (item.disabled) return

    if (item.isCreateOption && onCreate) {
      onCreate(searchText)
    } else {
      onChange([item])
    }
    setSearchText('')
    setIsOpen(false)
  }

  const handleInputFocus = () => {
    if (!isInteractive) return
    if (!isOpen) {
      setIsOpen(true)
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isInteractive) return
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchText('')
    } else if (e.key === 'Backspace' && !searchText && selectedValue) {
      onChange([])
    }
  }

  return (
    <ComboBoxContainer
      ref={containerRef}
      style={style}
      data-testid={dataTestId}
    >
      <ComboBoxToggle
        isOpen={isOpen}
        disabled={disabled}
        className={disabled ? 'disabled' : ''}
        onClick={handleToggleClick}
        style={readOnly && !disabled ? { cursor: 'default' } : undefined}
      >
        {isOpen ? (
          <ComboBoxInput
            ref={inputRef}
            value={searchText}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
            placeholder={selectedValue ? selectedValue.name : placeholder}
            disabled={disabled}
            readOnly={readOnly}
            data-testid={dataTestId ? `${dataTestId}-input` : undefined}
          />
        ) : (
          <ComboBoxDisplay
            className={!selectedValue ? 'placeholder' : ''}
            title={selectedValue?.name}
          >
            {selectedValue ? selectedValue.name : placeholder}
          </ComboBoxDisplay>
        )}
        <IconWrapper>
          <CustomIcons.faSearch
            size="xs"
            color={theme.colors.backgroundVeryDark}
            noHover={true}
          />
        </IconWrapper>
      </ComboBoxToggle>

      {isOpen && (filteredItems.length > 0 || (!allowCreate && searchText)) && (
        <ComboBoxMenu
          ref={menuRef}
          data-testid={dataTestId ? `${dataTestId}-menu` : undefined}
        >
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <ComboBoxMenuItem
                key={item.id}
                onClick={() => handleSelectItem(item)}
                className={`
                  ${selectedValue?.id === item.id ? 'selected' : ''}
                  ${item.isCreateOption ? 'create-option' : ''}
                  ${item.disabled ? 'disabled' : ''}
                `}
                style={{
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  opacity: item.disabled ? 0.5 : 1,
                }}
                title={item.tooltip || item.name}
                data-testid={
                  dataTestId ? `${dataTestId}-option-${item.name}` : undefined
                }
              >
                {item.isCreateOption ? (
                  item.name
                ) : (
                  <>
                    {hasIcons && item.icon && (
                      <ItemIcon src={item.icon} alt="" />
                    )}
                    <StringSearchMatchDisplayer
                      searchTerms={searchText.split(/\s+/).filter(Boolean)}
                    >
                      {item.name}
                    </StringSearchMatchDisplayer>
                    {item.tooltip && item.disabled && (
                      <span
                        style={{
                          fontSize: '0.85em',
                          color: '#667085',
                          marginLeft: '8px',
                        }}
                      >
                        ({item.tooltip})
                      </span>
                    )}
                  </>
                )}
              </ComboBoxMenuItem>
            ))
          ) : (
            <ComboBoxMenuItem
              className="no-results"
              style={{ cursor: 'default', color: '#667085' }}
            >
              No results found for "{searchText}"
            </ComboBoxMenuItem>
          )}
        </ComboBoxMenu>
      )}
    </ComboBoxContainer>
  )
}

export default StandardComboBox
